import { Suspense, useEffect } from "react";
import { baseUrl, translate, getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { SidebarSkeleton } from "./sidebar-skeleton/SidebarSkeleton";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { KmsTypeHeroProps } from "@kaltura/mediaspace-shared-types";
import { styled } from "@mui/material";
import { Box } from "@kaltura/mediaspace-shared-styled";
import CtaContainer from "./cta-container/CtaContainer";
import { useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";

export interface SidebarContainerProps extends Pick<KmsTypeHeroProps, "categoryId" | "playlist" | "entry"> {
    entryId: string;
    onEmptyData?: () => void;
    reloadCounter?: number;
}

/**
 * ts representation of Kms_Type_Media_Sidebar_Props
 */
type KmsTypeSidebarProps = {
    cta?: KmsTypeComponent[];
    sidebarTop?: KmsTypeComponent;
    sidebarLists?: KmsTypeComponent[];
};

interface SidebarProps {
    fetchData: () => KmsTypeSidebarProps;
    onEmptyData?: () => void;
    mediaName: string;
}

/**
 * media page Sidebar component -
 * render the relevant sidebar conponents as received from kms
 */
function Sidebar(props: SidebarProps) {
    const { mediaName, fetchData, onEmptyData } = props;
    const sidebarContent = fetchData();
    const { cta, sidebarTop, sidebarLists } = sidebarContent;

    const className = "kms-ds-media-page-sidebar";

    const renderComponent = (componentInfo: KmsTypeComponent, key?: string) => {
        const { component, props } = componentInfo;
        const Component = getComponentFromPath(component);
        return (
            <ErrorBoundary fallback={<></>} key={key}>
                <Component {...props} />
            </ErrorBoundary>
        );
    };

    const noSidebarTopAndLists = !sidebarTop && sidebarLists?.length === 0;

    useEffect(() => {
        noSidebarTopAndLists && onEmptyData?.();
    }, [noSidebarTopAndLists, onEmptyData]);

    if (noSidebarTopAndLists) {
        return <></>;
    }

    return (
        <section title={translate("Related content for %1", [mediaName])}>
            {cta && cta.length > 0 && (
                <CtaContainer
                    components={cta}
                    renderComponent={renderComponent}
                    className={className + "-cta"}
                    noSidebarTopAndLists={noSidebarTopAndLists}
                />
            )}
            {sidebarTop && (
                <StyledGapContainer className={className + "-top"}>{renderComponent(sidebarTop)}</StyledGapContainer>
            )}
            {sidebarLists && sidebarLists[0] && (
                <div className={className + "-list"}>{renderComponent(sidebarLists[0])}</div>
            )}
        </section>
    );
}

/**
 * media page Sidebar component container - load sidebar components from kms
 */
export function SidebarContainer(props: SidebarContainerProps) {
    const { entryId, categoryId, entry, playlist, onEmptyData, reloadCounter } = props;

    // media name - playlist/entry
    const mediaName = playlist ? playlist.name : entry.name;

    const url =
        baseUrl
        + "/default/media/sidebar/id/"
        + entryId
        + (categoryId ? "/categoryid/" + categoryId : "")
        + (playlist ? "/playlistid/" + playlist.id : "");

    const fetchData = useFetchKmsData<KmsTypeSidebarProps>({ url }, [url, reloadCounter]);

    return (
        <ErrorBoundary fallback={<SidebarSkeleton />}>
            <Suspense fallback={<SidebarSkeleton />}>
                <Sidebar mediaName={mediaName} fetchData={fetchData} onEmptyData={onEmptyData} />
            </Suspense>
        </ErrorBoundary>
    );
}

// A box that adds a margin between sidebar components
const StyledGapContainer = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
}));

export default SidebarContainer;
